/* eslint-disable max-len */
import {React, useCallback, useEffect, useState} from "react";
import YearTable from "./Components/YearTable";
import MonthTable from "./Components/MonthTable";
import Filter from "./Components/Filter";
import { GetBranchId } from "../../../../../../CommonMethods/GetBranchId";
import { dataService } from "../../../../../../services/data.services";
import Shimmer from '../../../../../../components/Shimmer/itemShimmer'
import extractMonthYearInRange from '../../../../../../CommonMethods/extractMonthYearInRange';


export default function AttendanceReports({tabs, searchParams, setSearchParams, setToastErrorMessage}) {
    const [loading, setLoading] = useState(false)
    const [ayAndClassOptions, setAyAndClassOptions] = useState([])
    const [sectionOptions, setSectionOptions] = useState([])
    const [reportsTotalCount, setReportsTotalCount] = useState(0)
    const [records, setRecords] = useState([])
    const [btnLoading, setBtnLoading] = useState(false)
    const [selectedAY, setSelectedAY] = useState([])
    const [selectedSection, setSelectedSection] = useState([])
    const [selectedMonth, setSelectedMonth] = useState('All Months')
    const [tableType, setTableType] = useState('yearly')
    const [monthOptions, setMonthOptions] = useState([]);
    const [ayStartDate, setAYStartDate] = useState([]);
    const [ayEndDate, setAYEndDate] = useState([]);

    /**
     * The function `getDefaultValue` returns a default value based on specified conditions from an array
     * of options.
     * @returns The function `getDefaultValue` returns different values based on the conditions provided:
     */
    const getDefaultValue = (options, firstIndex, paramsBased, useParams) => {
        if (!options || options?.length === 0) {
            return ""; // Return empty string if no objects
        }
        if (useParams) {
            if (paramsBased === 'ayAndClass') {
                const ayId = searchParams.get('ay_id');
                const classId = searchParams.get('class_id');
                if (ayId && classId) {
                    const selectedOption = options.find(option => option?.academic_year_id === ayId && option?.class_id === classId);
                    return selectedOption || ''; // Return matching option or empty string if not found
                }
            } else if (paramsBased === 'section') {
                const secId = searchParams.get('sec_id');
                if (secId) {
                    const selectedOption = options.find(option => option?.section_id === secId);
                    return selectedOption || ''; // Return matching option or empty string if not found
                }
            } 
        }
        if (options?.length === 1) {
            return options?.[0];
        }
        const latestOption = options?.find(option => option?.latest === true);
        if (latestOption) {
            return latestOption; // Return the object with the latest key set to true
        } else if (firstIndex) {
            return options?.[0] || ''; // Return the first object if all latest keys are false
        } else {
            return '';
        }
    };

    
    /* The `getAyAndClassOptions` function is a `useCallback` function that is responsible for fetching
    academic year and class options based on certain parameters. Here is a breakdown of what the
    function is doing: */
    const getAyAndClassOptions = useCallback(async () => {
        try {
            records?.length === 0 && setLoading(true);
            const branch_id = await GetBranchId();
            let endpoint = `students/list-ay-class?branch_id=${branch_id}&student_id=${searchParams?.get('std_uuid')}`;
            const response = await dataService.GetAPI(endpoint, true);
            const temp = response?.data?.data?.data;
            const temp2 = response?.data?.data?.total_count;

            setAyAndClassOptions(temp);
            const useParams = searchParams?.get('dropdown') === 'true';
            const selectedAYClass = await getDefaultValue(temp, true, 'ayAndClass', useParams);
            
            if (temp2 !== 0) {
                if (selectedAYClass === '') {
                    getSectionOptions(encodeURIComponent(searchParams?.get('ay_id')), encodeURIComponent(searchParams?.get('class_id')), false, useParams);
                } else {
                    setSelectedAY(selectedAYClass);
                    getSectionOptions(selectedAYClass, false);
                }
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
            setAyAndClassOptions([]);
            setToastErrorMessage(error?.response?.data?.message || "Something went wrong");
        }
    }, [searchParams]);    

    /* The `getSectionOptions` function is a `useCallback` function that is responsible for fetching
    section options based on the provided academic year ID and class ID. Here is a breakdown of what the
    function is doing: */
    const getSectionOptions = useCallback(async (selectedAYClass, fromDropDown) => {
        try {
            let ayID = selectedAYClass.academic_year?.academic_year_id
            let classID = selectedAYClass.class_id
            const branch_id = await GetBranchId();
            let endpoint = `students/list-sections?branch_id=${branch_id}&student_id=${searchParams?.get('std_uuid')}&academic_year_id=${ayID}&class_id=${classID}`;
            const response = await dataService.GetAPI(endpoint, true);
            const temp = response?.data?.data?.data;
            setSectionOptions(temp);
    
            const useParams = searchParams?.get('dropdown') === 'true';
            const selectedSection = await getDefaultValue(temp, true, 'section', useParams);
    
            if (selectedSection) {
                setSelectedSection(selectedSection);
                if(!fromDropDown){
                    getAttendanceReports(ayID, classID, selectedSection?.section_id,'All Months');
                }
                getMonthOptions(selectedAYClass, false);
            } else {
                setLoading(false)
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
            setSectionOptions([]);
            setToastErrorMessage(error?.response?.data?.message || "Something went wrong");
        }
    }, [searchParams, selectedAY]);
    
    const monthAbbreviationsToNumbers = {
        Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
        Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };

    const getMonthInfo = (monthAbbreviation) => {
        const [abbreviation, year] = monthAbbreviation.split(' ');
      
        const monthNumber = monthAbbreviationsToNumbers[abbreviation];
        if (monthNumber === undefined) {
            throw new Error('Invalid month abbreviation');
        }
      
        const date = new Date(year, monthNumber, 1);
        const fullMonthName = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
      
        return { monthNumber, fullMonthName };
    };

    /* The `getMonthOptions` function is a `useCallback` function that is responsible for fetching exam
    options based on the provided parameters. Here is a breakdown of what the function is doing: */
    const getMonthOptions = useCallback(async (selectedAYClass, fromDropDown) => {
        setAYStartDate(selectedAYClass.academic_year?.start_date)
        setAYEndDate(selectedAYClass.academic_year?.end_date)
        const array = extractMonthYearInRange(selectedAYClass.academic_year?.start_date, selectedAYClass.academic_year?.end_date)
        setMonthOptions(array)
    }, [searchParams, selectedAY]);    

    /* The `getAttendanceReports` function is a `useCallback` function that is defined to fetch academic
    reports data based on the provided parameters. Here is a breakdown of what the function is doing: */
    const getAttendanceReports = useCallback(async (ayID, classID, sectionID, monthName) => {
        try {   
            const branch_id = await GetBranchId()
            let endpoint = `student-attendance?branch_id=${branch_id}&student_id=${searchParams?.get('std_id')}&academic_year_id=${ayID}&class_id=${classID}&section_id=${sectionID}&filter=true`;
            monthName = (monthName === "" || monthName === undefined) ? "All Months" : monthName
            if(monthName === 'All Months')
            {
                endpoint += "&type=year"
                setTableType('yearly')
            }
            else{
                const { monthNumber, fullMonthName } = getMonthInfo(monthName);
                const year = monthName.split(' ')[1];
                endpoint += "&type=month&month="+fullMonthName+"&year="+year;
                setTableType('monthly')
            }
            const response = await dataService.GetAPI(endpoint)
            if (monthName === 'All Months') {
                const temp = response?.data?.data?.data[0]?.monthlyData;
                const temp2 = response?.data?.data?.total_count || 0
                setReportsTotalCount(temp2)
                setRecords(temp)
            }
            else {
                const temp = response?.data?.data?.data;
                setReportsTotalCount(temp.length)
                setRecords(temp)
            }
            
        } catch(error) {
            console.log(error)
            setLoading(false)
            setRecords([])
            setBtnLoading(false)
            setReportsTotalCount(0)
            //need to work to fix this better-Anitha
            if(error?.response?.data?.message == 'No records found' 
                || error?.response?.data?.message === 'Month and Year should be within the academic year')
            { return; }
            setToastErrorMessage(error?.response?.data?.message || "Something went wrong")
        } finally{
            setLoading(false)
            setBtnLoading(false)
        }
    }, []);

    /* The `useEffect` hook in the provided code snippet is used to perform side effects in function
    components. In this specific case: */
    useEffect(()=>{
        getAyAndClassOptions()
    }, [])

    return (
        <div className="">
            <div className="relative">
                <div
                    class="z-20 bg-white mt-5"
                    id="tabs-viewAcadamic_records"
                    role="tabpanel"
                    aria-labelledby="tabs-viewAcadamic_records-tab"
                >
                    <div className="pb-[32px] w-[850px] 2xl:px-0 2xl:w-[80%] mx-auto">
                        <Filter ayAndClassOptions={ayAndClassOptions} sectionOptions={sectionOptions} monthOptions={monthOptions} searchParams={searchParams}
                            getDefaultValue={getDefaultValue} getAttendanceReports={getAttendanceReports} loading={loading} reportsTotalCount={reportsTotalCount}
                            getSectionOptions={getSectionOptions} getMonthOptions={getMonthOptions} setSearchParams={setSearchParams} setLoading={setLoading}
                            btnLoading={btnLoading} setBtnLoading={setBtnLoading} selectedAY={selectedAY} setSelectedAY={setSelectedAY} selectedSection={selectedSection}
                            setSelectedSection={setSelectedSection} selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth} setToastErrorMessage={setToastErrorMessage}
                            ayEndDate={ayEndDate} ayStartDate={ayStartDate}
                        />
                    </div>     
                </div>
                {loading ? 
                    <div className='px-20'><Shimmer/> </div>
                    : 
                    tableType === "yearly" ? 
                        <YearTable data={{records, reportsTotalCount}} selectedAY={selectedAY} selectedSection={selectedSection} searchParams={searchParams}/> : 
                        <div class=" w-full px-20 relative">
                            <div class=" overflow-x-auto pb-2.5 scroll-bar -mx-[2px]">
                                <div class="flex px-[6px] mb-8" id="search-result">
                                    <MonthTable data={{ records, reportsTotalCount }} />
                                </div>
                            </div>
                        </div>
                }    
            </div>
        </div>
    );
}
